import { useMemo, useState } from "react";
import {
  buildCdLadder,
  flatRateCurve,
} from "../../lib/util/cd-ladder.js";
import {
  Card,
  SectionHeader,
  TextField,
} from "../../components/design-system/index.js";

/**
 * /banking/cd-ladder — Phase 8 Session 3.
 *
 * Interactive CD ladder builder. User enters total capital + rung
 * count + base term + step + flat rate; we compute equal-principal
 * rungs with maturity dates + projected interest.
 *
 * v1 uses a flat rate curve. A future version can pull live Treasury
 * par yields (e.g., from FRED's DGS3MO/DGS6MO/DGS1/DGS2/DGS3/DGS5 set)
 * for term-aware pricing. Out of scope for 8.3 — banking-hygiene v1
 * stays pure-frontend.
 */

export function BankingCdLadderPage() {
  const [totalDollars, setTotalDollars] = useState<string>("50000");
  const [rungs, setRungs] = useState<string>("5");
  const [baseTermMonths, setBaseTermMonths] = useState<string>("12");
  const [termStepMonths, setTermStepMonths] = useState<string>("12");
  const [apyPct, setApyPct] = useState<string>("4.50");

  const result = useMemo(() => {
    const total = Math.round(Number.parseFloat(totalDollars) * 100);
    const numRungs = Math.max(1, Number.parseInt(rungs, 10) || 1);
    const baseTerm = Math.max(1, Number.parseInt(baseTermMonths, 10) || 12);
    const stepTerm = Math.max(0, Number.parseInt(termStepMonths, 10) || 0);
    const apyBp = Math.max(0, Math.round(Number.parseFloat(apyPct) * 100));
    if (!Number.isFinite(total) || total < numRungs) return null;
    return buildCdLadder({
      total_cents: total,
      rungs: numRungs,
      base_term_months: baseTerm,
      term_step_months: stepTerm,
      rate_curve: flatRateCurve(apyBp),
    });
  }, [totalDollars, rungs, baseTermMonths, termStepMonths, apyPct]);

  return (
    <div className="mx-auto max-w-4xl p-6">
      <SectionHeader
        title="CD ladder builder"
        sub="Equal-principal-per-rung CD ladder. Stagger maturities so capital frees up regularly without locking the whole pile at the longest term."
      />
      <Card className="mb-4">
        <div className="flex flex-wrap gap-3">
          <TextField
            label="Total capital ($)"
            value={totalDollars}
            onChange={(e) => setTotalDollars(e.target.value)}
            type="number"
            min={0}
          />
          <TextField
            label="Rungs"
            value={rungs}
            onChange={(e) => setRungs(e.target.value)}
            type="number"
            min={1}
          />
          <TextField
            label="Base term (months)"
            value={baseTermMonths}
            onChange={(e) => setBaseTermMonths(e.target.value)}
            type="number"
            min={1}
          />
          <TextField
            label="Term step (months)"
            value={termStepMonths}
            onChange={(e) => setTermStepMonths(e.target.value)}
            type="number"
            min={0}
          />
          <TextField
            label="Flat APY (%)"
            value={apyPct}
            onChange={(e) => setApyPct(e.target.value)}
            type="number"
            step="0.01"
            min={0}
          />
        </div>
      </Card>

      {result && (
        <>
          <Card className="mb-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-xs text-[var(--color-muted)]">
                  Total principal
                </div>
                <div className="font-mono text-xl">
                  ${(result.total_principal_cents / 100).toFixed(0)}
                </div>
              </div>
              <div>
                <div className="text-xs text-[var(--color-muted)]">
                  Projected interest
                </div>
                <div className="font-mono text-xl">
                  ${(result.total_projected_interest_cents / 100).toFixed(0)}
                </div>
              </div>
              <div>
                <div className="text-xs text-[var(--color-muted)]">
                  Projected total at maturity
                </div>
                <div className="font-mono text-xl">
                  ${(result.total_projected_value_cents / 100).toFixed(0)}
                </div>
              </div>
              <div>
                <div className="text-xs text-[var(--color-muted)]">
                  Weighted-avg APY
                </div>
                <div className="font-mono text-xl">
                  {(result.weighted_avg_apy_bp / 100).toFixed(2)}%
                </div>
              </div>
            </div>
          </Card>

          <Card>
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-[var(--color-muted)]">
                <tr>
                  <th className="py-1">Rung</th>
                  <th>Term</th>
                  <th>Principal</th>
                  <th>APY</th>
                  <th>Maturity</th>
                  <th>Interest</th>
                  <th>Value at maturity</th>
                </tr>
              </thead>
              <tbody className="font-mono tabular-nums">
                {result.rungs.map((r) => (
                  <tr key={r.index} className="border-t border-[var(--color-border-subtle)]">
                    <td className="py-1">{r.index + 1}</td>
                    <td>{r.term_months} mo</td>
                    <td>${(r.principal_cents / 100).toFixed(0)}</td>
                    <td>{(r.apy_bp / 100).toFixed(2)}%</td>
                    <td>{r.maturity_date}</td>
                    <td>${(r.projected_interest_cents / 100).toFixed(0)}</td>
                    <td>${(r.projected_value_cents / 100).toFixed(0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}
